import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import Flatpickr from "react-flatpickr";
import { Alert, Button, Card, Radio, Toggle } from '@opendate-app/setlist';
import { toastSuccess } from "../../../shared/toastHelper";
import { offsetDateForBrowser } from "../../../shared/timeZoneLogic";

const DELIVERY_TYPES = [
  {
    name: "Immediate PDF Delivery",
    description: "Upon purchase the fan will receive tickets as a PDF.",
    value: "immediate_email",
    autoSave: true,
  },
  {
    name: "Immediate Electronic Delivery",
    description: "Upon purchase the fan will receive a link to add tickets to their digital wallet.",
    value: "immediate_electronic",
    autoSave: true,
  },
  {
    name: "Delayed PDF Delivery",
    description: "At a date you determine the fan will receive an email with tickets attached as a PDF.",
    value: "delayed_email",
    autoSave: false,
  },
  {
    name: "Delayed Electronic Delivery",
    description: "At a date you determine the fan will receive a link to add tickets to their digital wallet.",
    value: "delayed_electronic",
    autoSave: false,
  },
];

const CONFIRM_ON_SALE_CHANGE = "Changing delivery settings will affect all pending ticket deliveries. Continue?";

const DeliveryTab = ({
  csrfToken,
  team,
  confirm,
  updateConfirm,
  autoSaveConfirm,
}) => {
  const confirmOnSale = confirm.calendar_classification === "on_sale";
  const rotatingBarcodesEnabled = confirm.rotating_barcodes_enabled;
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const confirmRef = useRef(confirm);
  confirmRef.current = confirm;

  useEffect(() => {
    if (!unsavedChanges) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [unsavedChanges]);

  const selectDeliveryType = (value) => {
    const deliveryType = DELIVERY_TYPES.find((type) => type.value === value);
    const updated = {
      ...confirm,
      delivery_type: value,
      delivery_time: null
    };

    updateConfirm(updated);

    if (!deliveryType.autoSave) {
      setUnsavedChanges(true);
      return;
    }

    if (confirmOnSale && !window.confirm(CONFIRM_ON_SALE_CHANGE)) {
      updateConfirm(confirm);
      return;
    }

    autoSaveConfirm(csrfToken, team, updated, false, () => {
      toastSuccess("Delivery method saved");
      setUnsavedChanges(false);
    })
  };

  const toggleSkipTicketDelivery = (checked) => {
    if (confirmOnSale && !window.confirm(CONFIRM_ON_SALE_CHANGE)) {
      return;
    }

    const updated = { ...confirm, skip_ticket_delivery: checked };
    autoSaveConfirm(csrfToken, team, updated, false, () => {
      updateConfirm({ ...confirmRef.current, skip_ticket_delivery: checked });
      toastSuccess("Delivery method saved");
    }, { includeSkipTicketDelivery: true });
  };

  const saveDeliverySettings = () => {
    if (confirmOnSale && !window.confirm(CONFIRM_ON_SALE_CHANGE)) {
      return;
    }

    autoSaveConfirm(csrfToken, team, confirm, false, () => {
      toastSuccess("Delivery method saved");
      setUnsavedChanges(false);
    })
  };

  return (
    <div style={{fontSize: "14px", color: "var(--sl-text-primary)"}}>
      <div className="row mt-3">
        <div className="col-12">
          <h3 style={{fontSize: "18px"}}>
            <strong>
              Delivery
            </strong>
          </h3>
        </div>
      </div>
      <div className="mt-2 row">
        <div className="col-12">
          <p style={{color: "var(--sl-text-tertiary)"}}>Tickets will be delivered via email upon purchase unless delayed delivery is enabled.</p>
        </div>
      </div>
      {rotatingBarcodesEnabled ? (
        <div className="row mb-3">
          <div className="col-12 col-xl-7">
            <Alert
              color="gray"
              title="Rotating barcodes are active."
              description="This event uses electronic delivery, so fans receive secure, rotating barcodes. Switching to PDF delivery embeds a static barcode that cannot rotate."
            />
          </div>
        </div>
      ) : null}
      <div className="mt-2 row">
        <div className="col-12 col-xl-7">
          <div role="radiogroup" aria-label="Delivery method">
            {DELIVERY_TYPES.map((deliveryType) => (
              <div
                key={deliveryType.value}
                className="mb-2 p-3"
                style={{
                  borderRadius: "8px",
                  border: "2px solid " + (deliveryType.value === confirm.delivery_type ? "var(--sl-border-brand)" : "var(--sl-border-secondary)"),
                  background: "var(--sl-bg-primary)",
                }}
              >
                <Radio
                  name={`delivery-type-${deliveryType.value}`}
                  value={deliveryType.value}
                  checked={deliveryType.value === confirm.delivery_type}
                  onChange={() => selectDeliveryType(deliveryType.value)}
                  label={
                    <>
                      <strong style={{display: "block", color: "var(--sl-text-primary)"}}>{deliveryType.name}</strong>
                      <span style={{color: "var(--sl-text-tertiary)"}}>{deliveryType.description}</span>
                    </>
                  }
                />
              </div>
            ))}
          </div>
          {(team.mode === "AGENT_MANAGER" || confirm.skip_ticket_delivery) && (
            <div className="mt-3">
              <Toggle
                id="confirm-skip-ticket-delivery"
                label="Skip ticket delivery"
                checked={confirm.skip_ticket_delivery || false}
                onChange={(e) => toggleSkipTicketDelivery(e.target.checked)}
              />
              <p className="mt-1 mb-0" style={{color: "var(--sl-text-tertiary)"}}>
                Sell tickets without sending them. Tickets are delivered outside Opendate; buyers get an order confirmation saying their ticket will arrive from the venue closer to the event.
              </p>
            </div>
          )}
        </div>
        <div className="pl-xl-0 col-12 col-xl-5">
          {["delayed_email", "delayed_electronic"].includes(confirm.delivery_type) ? (
            <Card>
              <p>
                <strong>
                  {confirm.delivery_type === "delayed_email"
                    ? "Delayed Email Delivery"
                    : "Delayed Electronic Delivery"}
                </strong>
              </p>
              <p style={{color: "var(--sl-text-tertiary)"}}>Specify the date on which you would like tickets to be delivered.</p>
              <p style={{color: "var(--sl-text-tertiary)"}}>Changing delivery settings will update all pending orders.</p>
              <div className="d-flex align-items-end" style={{gap: "8px"}}>
                <div style={{flex: 1}}>
                  <label htmlFor="delivery-time"
                         className="d-block mb-1"
                         style={{fontSize: "14px", fontWeight: 500, color: "var(--sl-text-secondary)"}}>
                    Date of delivery<span style={{color: "var(--sl-text-error)"}}> *</span>
                  </label>
                  {!confirm.delivery_time || confirm.delivery_time instanceof Date ? (
                    <Flatpickr
                      id="delivery-time"
                      options={{
                        dateFormat: "M j, Y at h:iK",
                        enableTime: true,
                        minDate: offsetDateForBrowser(new Date()),
                        onOpen: function(selectedDates, dateStr, instance) {
                          instance.set("minDate", offsetDateForBrowser(new Date()));
                        },
                      }}
                      placeholder={"Choose date & time"}
                      className={"form-control ticketing-date-picker form-control-flatpickr"}
                      style={{fontSize: "14px"}}
                      value={confirm.delivery_time}
                      onClose={dates => {
                        if (dates.length === 0) return;

                        updateConfirm({...confirm, delivery_time: dates[0]});
                        setUnsavedChanges(true);
                      }}
                    />
                  ) : null}
                </div>
                <Button
                  variant="primary"
                  disabled={!confirm.delivery_time}
                  onClick={saveDeliverySettings}
                >
                  Save
                </Button>
              </div>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}

DeliveryTab.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  updateConfirm: PropTypes.func.isRequired,
  autoSaveConfirm: PropTypes.func.isRequired,
};

export default DeliveryTab;
